import type {
  FieldDef,
  FormSchema,
  QuestionDef,
} from "../risikoanalyseStep/types";
import { MANUFACTURERS } from "./manufacturers";
import { VEHICLE_CLASSES } from "./vehicleClasses";
import { VEHICLE_USAGE_CLASSES } from "./verwendungen";
import { WKZ_CODES } from "./wkzCodes";

/**
 * Schema for the public KFZ form. Field keys match the legacy kfzForm
 * (data.ts) so the back-office can keep using the existing
 * `saveKfzRiskObject` translation when an admin promotes a token's
 * answers to a real RiskObject.
 */

const yn = (
  question: string,
  key: string,
  followUp?: FieldDef[],
  hint?: string
): QuestionDef => ({
  question,
  hint,
  field: { key, type: "yesno" },
  ...(followUp
    ? {
        showFollowUpWhen: { key, equals: "ja" },
        followUpFields: followUp,
      }
    : {}),
});

const q = (question: string, field: FieldDef, hint?: string): QuestionDef => ({
  question,
  hint,
  field,
});

const qMulti = (
  question: string,
  fields: FieldDef[],
  opts?: { hint?: string; layout?: "one" | "two" }
): QuestionDef => ({
  question,
  hint: opts?.hint,
  layout: opts?.layout,
  fields,
});

const VEHICLE_CLASS_SELECT = VEHICLE_CLASSES.map((c) => ({
  value: c.value,
  label: c.short + " — " + c.description,
}));

// Vehicle classes that surface the Auf-/Einbauten section (Excel R21).
const BODY_TYPE_CLASSES = [
  "Klasse 2 Lieferwagen Güterbeförderung bis 3.500kg",
  "Klasse 3 LKW Güterbeförderung über 3.500kg",
  "Klasse 6 Anhänger/Auflieger",
];

// Fahrzeugklassen MIT Fahrerdaten (Fahrername / Fahreralter / Fahrleistung /
// Führerschein) nach der Excel-Matrix R33-37. Achtung, das ist eine
// Whitelist und sie ist enger, als man denkt: Neben den Klassen 6
// (Anhänger), 7 (Arbeitsmaschinen) und 8 (Gabelstapler) fehlen hier auch
// 3 (LKW), 4 (Zugmaschine) und 5 (Busse). Für diese drei zeigt das Formular
// die Fahrerfragen nicht — und seit der Fortschritt dieselbe Sichtbarkeit
// nutzt, gilt ein LKW-Formular ohne Fahrerangaben als vollständig. Ob das
// fachlich so gewollt ist, ist offen; geändert wird die Liste nur zusammen
// mit der Fach-Excel.
const DRIVER_DATA_VISIBLE_CLASSES = [
  "Klasse 1 PKW",
  "Klasse 2 Lieferwagen Güterbeförderung bis 3.500kg",
  "Klasse 9 Krad",
  "Klasse 10 Sonderfahrzeuge",
];

// driverCircle is only relevant for Klasse 1 & 2 per Excel R34.
const DRIVER_CIRCLE_VISIBLE_CLASSES = [
  "Klasse 1 PKW",
  "Klasse 2 Lieferwagen Güterbeförderung bis 3.500kg",
];

export const kfzStepFormSchema: FormSchema = {
  sections: [
    {
      key: "kfz_main",
      title: "KFZ-Erfassung",
      color: "pillarOrange",
      subSteps: [
        // 1. Fahrzeugart — visual tile picker
        {
          key: "fahrzeugart",
          title: "Fahrzeugart",
          description:
            "Bitte wählen Sie die Fahrzeugklasse, die das zu erfassende Fahrzeug am besten beschreibt.",
          questions: [
            q("Um welche Fahrzeugart handelt es sich?", {
              key: "vehicleClass",
              label: "Fahrzeugklasse",
              type: "select",
              options: VEHICLE_CLASS_SELECT,
            }),
          ],
        },

        // 1b. Verwendung — visuelle Tile-Auswahl, klassenabhängig (custom render in page)
        {
          key: "verwendung",
          title: "Verwendung",
          description:
            "Wie wird das Fahrzeug genutzt? Die Optionen passen sich an die gewählte Fahrzeugart an.",
          questions: [
            {
              // Ohne Verwendungskatalog zeigt der VerwendungPicker nur einen
              // Hinweis — der Nutzer kann `vehicleUsage` dann gar nicht
              // befüllen. Ungegatet zählte das Feld trotzdem im Nenner des
              // Fortschritts mit, und ein zu Ende ausgefülltes Formular blieb
              // bei den Klassen 5, 7, 8 und 9 bei 97 % stehen.
              // Deckt sich mit der Fach-Liste: RISIKO_QUESTIONS in
              // kfzContractStep/data.ts führt „Verwendung" ebenfalls nur bei
              // PKW, Lieferwagen, LKW, Zugmaschine und Anhänger. Damit fällt
              // das Feld auch in der Risiko-Detailansicht weg, die diesen
              // Schritt über QuestionBlock als Freitext rendert — genau bei
              // den Klassen, für die die Fach-Liste keine Verwendung kennt.
              question: "Welche Verwendung hat das Fahrzeug?",
              showWhen: {
                key: "vehicleClass",
                equalsAny: VEHICLE_USAGE_CLASSES,
              },
              field: {
                key: "vehicleUsage",
                label: "Verwendung",
                type: "text",
              },
            },
          ],
        },

        // 2. Kennzeichen
        {
          key: "kennzeichen",
          title: "Kennzeichen",
          description:
            "Bitte geben Sie das amtliche Kennzeichen sowie die Zulassungsangaben an.",
          questions: [
            q("Wie lautet das Kennzeichen?", {
              key: "licensePlate",
              label: "Kennzeichen",
              type: "text",
              placeholder: "z. B. AC-XP 123",
            }),
            {
              question: "Handelt es sich um einen Klassiker?",
              showWhen: { key: "vehicleClass", equals: "Klasse 1 PKW" },
              field: {
                key: "classicType",
                label: "Klassikertyp",
                type: "select",
                options: [
                  { value: "", label: "— nicht zutreffend —" },
                  { value: "Youngtimer > 20", label: "Youngtimer > 20 Jahre" },
                  { value: "Oldtimer > 30", label: "Oldtimer > 30 Jahre" },
                ],
              },
            },
            {
              question: "Liegt ein H-Kennzeichen vor?",
              showWhen: { key: "classicType", equals: "Oldtimer > 30" },
              field: { key: "hLicensePlate", type: "yesno" },
            },
            q("Wie ist das Fahrzeug zugelassen?", {
              key: "registrationType",
              label: "Zulassungsart",
              type: "select",
              options: [
                { value: "Dauerhaft", label: "Dauerhaft" },
                { value: "Saison", label: "Saisonkennzeichen" },
              ],
            }),
            {
              question: "Saison von / bis (Monate)",
              showWhen: { key: "registrationType", equals: "Saison" },
              field: {
                key: "seasonStartEnd",
                label: "Saison-Zeitraum",
                type: "text",
                placeholder: "z. B. 04/10",
              },
            },
            q("Welche Kennzeichenart hat das Fahrzeug?", {
              key: "licensePlateType",
              label: "Kennzeichenart",
              type: "select",
              options: [
                { value: "Schwarz", label: "Schwarz (regulär)" },
                { value: "Grün", label: "Grün (steuerbefreit)" },
                { value: "Wechselkennzeichen", label: "Wechselkennzeichen" },
              ],
            }),
          ],
        },

        // 3. Marke / Modell
        {
          key: "marke_modell",
          title: "Marke & Modell",
          description:
            "Marke, Modell und technische Eckdaten des Fahrzeugs.",
          questions: [
            q("Welche Marke hat das Fahrzeug?", {
              key: "manufacturer",
              label: "Hersteller",
              type: "select",
              options: MANUFACTURERS.map((m) => ({ value: m, label: m })),
            }),
            qMulti(
              "Welches Modell und welche Schlüsselnummern? (Fahrzeugschein)",
              [
                { key: "typeName", label: "Typname / Modell", type: "text" },
                { key: "hsn", label: "Herstellernummer (HSN)", type: "text" },
                { key: "tsn", label: "Typschlüsselnummer (TSN)", type: "text" },
                { key: "vin", label: "Fahrzeugidentnummer (VIN)", type: "text" },
              ],
              { layout: "two" }
            ),
            q("Welche Antriebsart hat das Fahrzeug?", {
              key: "driveType",
              label: "Antrieb",
              type: "select",
              options: [
                { value: "Verbrenner", label: "Verbrenner" },
                { value: "Hybrid", label: "Hybrid" },
                { value: "Elektro", label: "Elektro" },
                { value: "Gas", label: "Gas" },
                { value: "Wasserstoff", label: "Wasserstoff" },
              ],
            }),
            {
              question: "Liegt ein E-Kennzeichen vor?",
              showWhen: { key: "driveType", equals: "Elektro" },
              field: { key: "eLicensePlate", type: "yesno" },
            },
            qMulti("Wie sind die technischen Eckdaten?", [
              { key: "powerKw", label: "Motorleistung in kW", type: "text" },
              {
                key: "grossWeight",
                label: "Gesamtgewicht in kg",
                type: "text",
              },
              { key: "payload", label: "Nutzlast in kg", type: "text" },
            ]),
            yn(
              "Gibt es eine nähere Fahrzeugspezifikation?",
              "hasSpecs",
              [
                {
                  key: "specs",
                  label: "Welche Spezifikation?",
                  type: "textarea",
                },
              ]
            ),
          ],
        },

        // 4. Auf- und Einbauten
        {
          key: "aufbauten",
          title: "Auf- und Einbauten",
          description:
            "Sonderaufbauten und Einbauten — z. B. Kipper, Kasten, Werkstatt-Equipment.",
          questions: [
            {
              question: "Welche Aufbaubezeichnung trifft zu?",
              showWhen: {
                key: "vehicleClass",
                equalsAny: BODY_TYPE_CLASSES,
              },
              field: {
                key: "bodyType",
                label: "Aufbaubezeichnung",
                type: "select",
                options: [
                  { value: "", label: "— nicht zutreffend —" },
                  { value: "Kipper", label: "Kipper" },
                  { value: "offener Kasten", label: "Offener Kasten" },
                  {
                    value: "offener Kasten mit Plane und Spriegel",
                    label: "Offener Kasten mit Plane und Spriegel",
                  },
                  { value: "geschlossener Kasten", label: "Geschlossener Kasten" },
                  { value: "Wechselaufbau", label: "Wechselaufbau" },
                  { value: "sonstiger Aufbau", label: "Sonstiger Aufbau" },
                ],
              },
            },
            yn(
              "Gibt es Einbauten im Fahrzeug?",
              "hasInstallations",
              [
                {
                  key: "installationsDetails",
                  label: "Welche Einbauten?",
                  type: "textarea",
                },
              ]
            ),
          ],
        },

        // 5. Wert
        {
          key: "wert",
          title: "Wert & Anschaffung",
          description:
            "Werte und Art der Anschaffung des Fahrzeugs.",
          questions: [
            q("Wie hoch ist der Wert des Fahrzeugs (Neu- bzw. Zeitwert)?", {
              key: "vehicleValue",
              label: "Fahrzeugwert (€)",
              type: "text",
              placeholder: "0,00",
            }),
            q("Wie hoch ist der Wert der Beschriftung?", {
              key: "labelingValue",
              label: "Beschriftung (€)",
              type: "text",
              placeholder: "0,00",
            }),
            {
              question: "Wie hoch ist der Wert der Einbauten?",
              showWhen: { key: "hasInstallations", equals: "ja" },
              field: {
                key: "installationsValue",
                label: "Einbauten (€)",
                type: "text",
                placeholder: "0,00",
              },
            },
            {
              question: "Wie hoch ist der Wert der Aufbauten?",
              showWhen: { key: "bodyType", equals: "sonstiger Aufbau" },
              field: {
                key: "bodyValue",
                label: "Aufbauten (€)",
                type: "text",
                placeholder: "0,00",
              },
            },
            {
              question: "Marktwert gemäß Gutachten?",
              hint: "Nur bei Young- oder Oldtimern relevant.",
              showWhen: { key: "classicType", notEquals: "" },
              fields: [
                {
                  key: "marketValue",
                  label: "Marktwert (€)",
                  type: "text",
                  placeholder: "0,00",
                },
                {
                  key: "marketValueDate",
                  label: "Gutachten vom",
                  type: "date",
                },
              ],
            },
            {
              question: "Wiederaufbauwert gemäß Gutachten?",
              hint: "Nur bei Young- oder Oldtimern; ggf. Hinweis auf Zusatzdeckung.",
              showWhen: { key: "classicType", notEquals: "" },
              fields: [
                {
                  key: "restorationValue",
                  label: "Wiederaufbauwert (€)",
                  type: "text",
                  placeholder: "0,00",
                },
                {
                  key: "restorationValueDate",
                  label: "Gutachten vom",
                  type: "date",
                },
              ],
            },
            q("Wie wurde das Fahrzeug angeschafft?", {
              key: "acquisitionType",
              label: "Anschaffungsart",
              type: "select",
              options: [
                { value: "Kauf", label: "Kauf" },
                { value: "Mietkauf", label: "Mietkauf" },
                { value: "Leasing", label: "Leasing" },
              ],
            }),
            // ── Kauf
            {
              question: "Zustand beim Kauf?",
              showWhen: { key: "acquisitionType", equals: "Kauf" },
              field: {
                key: "purchaseCondition",
                label: "Zustand",
                type: "select",
                options: [
                  { value: "Neu", label: "Neu" },
                  { value: "Gebraucht", label: "Gebraucht" },
                ],
              },
            },
            {
              question: "Wie hoch war der Listen- bzw. Kaufpreis?",
              showWhen: { key: "acquisitionType", equals: "Kauf" },
              field: {
                key: "purchasePrice",
                label: "Preis (€)",
                type: "text",
                placeholder: "0,00",
              },
            },
            // ── Mietkauf
            {
              question: "Mietkauf-Konditionen",
              showWhen: { key: "acquisitionType", equals: "Mietkauf" },
              fields: [
                {
                  key: "hirePurchasePayment",
                  label: "Mietzahlung (€)",
                  type: "text",
                  placeholder: "0,00",
                },
                {
                  key: "hirePurchaseDuration",
                  label: "Laufzeit (Monate)",
                  type: "text",
                  placeholder: "z. B. 36",
                },
                {
                  key: "hirePurchaseContractNum",
                  label: "Vertragsnummer",
                  type: "text",
                },
              ],
              layout: "two",
            },
            // ── Leasing
            {
              question: "Leasing-Konditionen",
              showWhen: { key: "acquisitionType", equals: "Leasing" },
              fields: [
                {
                  key: "leasingRate",
                  label: "Leasingrate (€)",
                  type: "text",
                  placeholder: "0,00",
                },
                {
                  key: "leasingDuration",
                  label: "Laufzeit (Monate)",
                  type: "text",
                  placeholder: "z. B. 48",
                },
                {
                  key: "leasingContractNum",
                  label: "Vertragsnummer",
                  type: "text",
                },
              ],
              layout: "two",
            },
            {
              question: "Sonstiges zur Anschaffung?",
              showWhen: {
                key: "acquisitionType",
                equalsAny: ["Mietkauf", "Leasing"],
              },
              field: {
                key: "acquisitionNotes",
                label: "Anmerkungen",
                type: "textarea",
              },
            },
            yn("Wird das Fahrzeug als Betriebsausgabe geltend gemacht?", "expense"),
          ],
        },

        // 6. Zulassung und Verwendung — Excel rows 26-32
        {
          key: "zulassung_verwendung",
          title: "Zulassung & Verwendung",
          description:
            "Erstzulassung, Halter sowie Sonderfälle bei der Verwendung des Fahrzeugs.",
          questions: [
            qMulti(
              "Erstzulassung und Erstzulassung auf den Versicherungsnehmer",
              [
                {
                  key: "firstRegistration",
                  label: "Erstzulassung",
                  type: "date",
                },
                {
                  key: "firstOwnerRegistration",
                  label: "Erstzulassung auf VN",
                  type: "date",
                },
              ],
              { layout: "two" }
            ),
            q("Gibt es einen abweichenden Halter?", {
              key: "otherKeeper",
              label: "Name des abweichenden Halters",
              type: "text",
              placeholder: "leer lassen, wenn nicht zutreffend",
            }),
            yn(
              "Liegt das Fahrgebiet außerhalb der EU?",
              "hasDrivingAreaOutsideEU",
              [
                {
                  key: "drivingArea",
                  label: "Welche Gebiete?",
                  type: "text",
                },
              ]
            ),
            yn(
              "Befindet sich das Fahrzeug auf Mallorca?",
              "vehicleOnMallorca"
            ),
            yn(
              "Werden Touristenfahrten auf Rennstrecken durchgeführt?",
              "raceTrack",
              [
                {
                  key: "raceTrackDetails",
                  label: "Welche Rennstrecken?",
                  type: "text",
                },
              ]
            ),
          ],
        },

        // 7. Fahrerdaten — Excel rows 33-38
        {
          key: "fahrerdaten",
          title: "Fahrerdaten",
          description:
            "Angaben zum Fahrerkreis, Alter, Fahrleistung und Führerschein. Welche Felder sichtbar sind, hängt von der Fahrzeugklasse ab.",
          questions: [
            {
              question: "Wer ist Hauptfahrer?",
              showWhen: {
                key: "vehicleClass",
                equalsAny: DRIVER_DATA_VISIBLE_CLASSES,
              },
              field: {
                key: "mainDriverName",
                label: "Fahrername",
                type: "text",
              },
            },
            {
              question: "Wie ist der Fahrerkreis?",
              showWhen: {
                key: "vehicleClass",
                equalsAny: DRIVER_CIRCLE_VISIBLE_CLASSES,
              },
              field: {
                key: "driverCircle",
                label: "Fahrerkreis",
                type: "select",
                options: [
                  { value: "VN", label: "VN" },
                  { value: "VN + Partner", label: "VN + Partner" },
                  { value: "VN + Kind", label: "VN + Kind" },
                  { value: "VN + Partner + Kinder", label: "VN + Partner + Kinder" },
                  { value: "Sonstige", label: "Sonstige" },
                ],
              },
            },
            {
              question: "Wie alt ist der Hauptfahrer?",
              showWhen: {
                key: "vehicleClass",
                equalsAny: DRIVER_DATA_VISIBLE_CLASSES,
              },
              field: {
                key: "mainDriverAge",
                label: "Fahreralter",
                type: "select",
                options: [
                  { value: "unter 23", label: "unter 23" },
                  { value: "unter 65", label: "unter 65" },
                  { value: "unter 70", label: "unter 70" },
                  { value: "unter 75", label: "unter 75" },
                  { value: "unter 80", label: "unter 80" },
                  { value: "über 80", label: "über 80" },
                ],
              },
            },
            {
              question: "Wie hoch ist die jährliche Fahrleistung?",
              showWhen: {
                key: "vehicleClass",
                equalsAny: DRIVER_DATA_VISIBLE_CLASSES,
              },
              field: {
                key: "mileage",
                label: "Jährliche Fahrleistung (km)",
                type: "select",
                options: [
                  { value: "bis 6.000", label: "bis 6.000" },
                  { value: "bis 9.000", label: "bis 9.000" },
                  { value: "bis 12.000", label: "bis 12.000" },
                  { value: "bis 15.000", label: "bis 15.000" },
                  { value: "bis 18.000", label: "bis 18.000" },
                  { value: "bis 24.000", label: "bis 24.000" },
                  { value: "bis 30.000", label: "bis 30.000" },
                  { value: "über 30.000", label: "über 30.000" },
                ],
              },
            },
            q("Wann wurde der Führerschein erworben?", {
              key: "licenseDate",
              label: "Führerscheindatum",
              type: "date",
            }),
            yn(
              "Begleitetes Fahren (BF17)?",
              "accompaniedDriving",
              [
                {
                  key: "accompaniedDrivingDate",
                  label: "Bis wann?",
                  type: "date",
                },
              ]
            ),
          ],
        },

        // 8. WKZ — Excel sheet WKZs (56 Codes)
        {
          key: "wkz",
          title: "Wagniskennziffer (WKZ)",
          description:
            "Die WKZ wird nach erfolgter Generali-Tarifierung automatisch befüllt. Bis dahin kann sie manuell aus der Liste ausgewählt werden.",
          questions: [
            q("Welche Wagniskennziffer trifft zu?", {
              key: "wkz",
              label: "WKZ",
              type: "select",
              options: [
                { value: "", label: "— noch nicht bekannt —" },
                ...WKZ_CODES.map((w) => ({
                  value: w.code,
                  label: `${w.code} — ${w.label}`,
                })),
              ],
            }),
          ],
        },
      ],
    },
  ],
};
